import { formOptions } from '@tanstack/react-form';
import { z } from 'zod';

import { notificationChannelSchema } from '@/entities/notification';
import {
  WORKFLOW_CHANNEL_TYPE,
  WORKFLOW_REALTIME_BASIS,
} from '@/types/workflowConst';

// Realtime 폼 스키마 (backend RealtimeWorkflowState + base fields 구조와 일치)
export const realtimeFormSchema = z
  .object({
    title: z.string().min(1, { message: 'Please enter a title' }),
    channel: notificationChannelSchema,
    // RealtimeWorkflowState 필드들
    basis: z.union([
      z.literal(WORKFLOW_REALTIME_BASIS.EVERY),
      z.literal(WORKFLOW_REALTIME_BASIS.SELECTED_FILTER),
    ]),
    filterset: z.record(z.string(), z.unknown()),
  })
  .refine(
    // basis가 SELECTED_FILTER면 filterset이 비어있지 않아야 함
    (data) =>
      data.basis === WORKFLOW_REALTIME_BASIS.EVERY ||
      Object.keys(data.filterset).length > 0,
    {
      message: 'Please select at least one filter',
      path: ['filterset'],
    },
  )
  .refine(
    // basis가 EVERY면 MAIL 채널 사용 불가
    (data) =>
      data.basis !== WORKFLOW_REALTIME_BASIS.EVERY ||
      data.channel.type !== WORKFLOW_CHANNEL_TYPE.MAIL,
    {
      message: 'Email channel is not available for "Every feedback" option',
      path: ['channel'],
    },
  );

export type RealtimeFormData = z.infer<typeof realtimeFormSchema>;

// 기본값
export const realtimeFormDefaultValues: RealtimeFormData = {
  title: '',
  channel: {
    type: WORKFLOW_CHANNEL_TYPE.SLACK,
    slack_webhook_id: '',
  },
  basis: WORKFLOW_REALTIME_BASIS.EVERY,
  filterset: {},
};

// 폼 검증 함수
const validateRealtimeForm = ({ value }: { value: RealtimeFormData }) => {
  const result = realtimeFormSchema.safeParse(value);
  if (result.success) return undefined;

  return result.error.issues.map((issue) => ({
    path: issue.path,
    message: issue.message,
  }));
};

export const realtimeFormOptions = formOptions({
  defaultValues: realtimeFormDefaultValues,
  validators: {
    onSubmit: validateRealtimeForm,
  },
});
